// src/admin/AdminSampleData.js
import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  query,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';
import allotmentService from '../services/allotmentService';

function AdminSampleData() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState(null);

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      const routesSnapshot = await getDocs(
        query(collection(db, 'routes'), orderBy('name'))
      );
      const routesData = routesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setRoutes(routesData);
    } catch (error) {
      console.error('Error fetching routes:', error);
      alert('Error fetching routes: ' + error.message);
    }
  };

  // Sample daily allowance generation functions
  const getDailyAllowanceForRoute = (routeName) => {
    // Daily budget amounts that BackPass will cover for room bookings (in USD)
    // Realistic Albanian accommodation rates averaging ~$15/night
    const routeDailyAllowances = {
      'Albania Adventure': { base: 14, peak: 20, weekend: 17, midweek: 12 },
      'Balkan Explorer': { base: 15, peak: 22, weekend: 18, midweek: 13 },
      'Mountain Trek': { base: 12, peak: 18, weekend: 15, midweek: 10 },
      'Coastal Journey': { base: 18, peak: 25, weekend: 21, midweek: 16 },
      'Cultural Discovery': { base: 16, peak: 23, weekend: 19, midweek: 14 }
    };
    
    return routeDailyAllowances[routeName] || { base: 15, peak: 22, weekend: 18, midweek: 13 };
  };

  const isPeakSeason = (date) => {
    const month = date.getMonth() + 1; // 1-12
    const day = date.getDate();
    
    // Peak summer season (high demand)
    if (month === 8 && day >= 15) return true; // Mid to late August
    if (month === 9 && day <= 15) return true; // Early to mid September
    
    // Assumption Festival (Aug 15) - major holiday in many European countries
    if (month === 8 && day === 15) return true;
    
    return false;
  };

  const isWeekend = (date) => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 5 || dayOfWeek === 6; // Friday or Saturday
  };

  const isMidweek = (date) => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 2 || dayOfWeek === 3; // Tuesday or Wednesday
  };

  const getDateType = (date) => {
    if (isPeakSeason(date)) return 'peak';
    if (isWeekend(date)) return 'weekend';
    if (isMidweek(date)) return 'midweek';
    return 'base';
  };

  const addRandomVariation = (baseValue, maxVariation = 0.2) => {
    const variation = (Math.random() - 0.5) * 2 * maxVariation;
    return Math.round(baseValue * (1 + variation));
  };

  const generateAllowanceData = (routeId, routeName, date) => {
    const dateType = getDateType(date);
    const dailyAllowances = getDailyAllowanceForRoute(routeName);
    
    let dailyAllowance = dailyAllowances[dateType];
    
    // Add some random variation to make data more realistic
    dailyAllowance = addRandomVariation(dailyAllowance, 0.15);
    
    // Ensure minimum values (minimum $8 daily allowance for budget Albanian accommodation)
    dailyAllowance = Math.max(8, dailyAllowance);
    
    const isLocked = Math.random() < 0.1; // 10% chance of being locked
    
    const reasonMap = {
      'peak': 'high summer demand',
      'weekend': 'weekend premium',
      'midweek': 'midweek discount',
      'base': 'standard rate'
    };
    
    return {
      dailyAllowance,
      baseCurrency: 'USD',
      isLocked,
      reason: `Sample data - ${reasonMap[dateType]} (Albanian hostel/guesthouse rates)`
    };
  };

  const populateSampleData = async () => {
    if (routes.length === 0) {
      alert('No routes found! Please create some routes first.');
      return;
    }

    if (!window.confirm(`This will create sample daily allowance data for August and September 2025 across ${routes.length} routes. This will create approximately ${routes.length * 62} records. Continue?`)) {
      return;
    }

    setLoading(true);
    setProgress('Starting sample data generation...');
    setResults(null);

    try {
      // Generate dates for August and September 2025
      const dates = [];
      
      // August 2025
      for (let day = 1; day <= 31; day++) {
        dates.push(new Date(2025, 7, day)); // Month is 0-indexed
      }
      
      // September 2025
      for (let day = 1; day <= 30; day++) {
        dates.push(new Date(2025, 8, day));
      }

      let totalRecords = 0;
      let routeCount = 0;

      // Process each route
      for (const route of routes) {
        routeCount++;
        setProgress(`Processing route ${routeCount}/${routes.length}: ${route.name}...`);
        
        let routeRecords = 0;
        const batchSize = 20;
        
        // Process dates in batches
        for (let i = 0; i < dates.length; i += batchSize) {
          const dateBatch = dates.slice(i, i + batchSize);
          const batchPromises = [];
          
          for (const date of dateBatch) {
            const dateStr = date.toISOString().split('T')[0];
            const allowanceData = generateAllowanceData(route.id, route.name, date);
            
            const promise = allotmentService.setAllowance(
              route.id,
              dateStr,
              allowanceData,
              'admin-sample-data'
            // eslint-disable-next-line no-loop-func
            ).then(() => {
              routeRecords++;
              totalRecords++;
            }).catch((error) => {
              console.error(`Error creating daily allowance for ${route.name} on ${dateStr}:`, error);
            });
            
            batchPromises.push(promise);
          }
          
          // Wait for batch to complete
          await Promise.all(batchPromises);
          
          setProgress(`Processing route ${routeCount}/${routes.length}: ${route.name} - ${Math.min(i + batchSize, dates.length)}/${dates.length} dates completed`);
        }
        
        console.log(`Completed ${routeRecords} records for ${route.name}`);
      }

      setResults({
        totalRecords,
        routeCount: routes.length,
        dateCount: dates.length,
        success: true
      });
      
      setProgress('Sample data generation completed successfully!');
      
    } catch (error) {
      console.error('Error generating sample data:', error);
      setResults({
        error: error.message,
        success: false
      });
      setProgress('Error occurred during sample data generation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-sample-data">
      <style>{`
        .admin-sample-data {
          padding: 20px;
          max-width: 800px;
        }
        
        .page-header {
          margin-bottom: 30px;
        }
        
        .page-title {
          font-size: 24px;
          font-weight: bold;
          color: #333;
          margin-bottom: 10px;
        }
        
        .page-description {
          color: #666;
          line-height: 1.5;
        }
        
        .info-section {
          background: #f8f9fa;
          padding: 20px;
          border-radius: 8px;
          margin-bottom: 30px;
        }
        
        .info-title {
          font-size: 18px;
          font-weight: bold;
          color: #333;
          margin-bottom: 15px;
        }
        
        .info-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .info-list li {
          margin-bottom: 8px;
          padding-left: 20px;
          position: relative;
        }
        
        .info-list li:before {
          content: '•';
          color: #007bff;
          font-weight: bold;
          position: absolute;
          left: 0;
        }
        
        .routes-section {
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 30px;
        }
        
        .routes-title {
          font-size: 16px;
          font-weight: bold;
          margin-bottom: 15px;
          color: #333;
        }
        
        .routes-list {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
          gap: 10px;
        }
        
        .route-item {
          padding: 10px;
          background: #f8f9fa;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .no-routes {
          color: #dc3545;
          font-style: italic;
        }
        
        .action-section {
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 30px;
        }
        
        .generate-button {
          background: #28a745;
          color: white;
          border: none;
          padding: 15px 30px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 16px;
          font-weight: bold;
          margin-right: 15px;
        }
        
        .generate-button:hover:not(:disabled) {
          background: #218838;
        }
        
        .generate-button:disabled {
          background: #6c757d;
          cursor: not-allowed;
        }
        
        .progress-section {
          margin-top: 20px;
          padding: 15px;
          background: #e3f2fd;
          border-radius: 4px;
          border-left: 4px solid #2196f3;
        }
        
        .progress-text {
          color: #1976d2;
          font-weight: bold;
        }
        
        .results-section {
          margin-top: 20px;
          padding: 20px;
          border-radius: 8px;
        }
        
        .results-success {
          background: #d4edda;
          border: 1px solid #c3e6cb;
          color: #155724;
        }
        
        .results-error {
          background: #f8d7da;
          border: 1px solid #f5c6cb;
          color: #721c24;
        }
        
        .results-title {
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 15px;
        }
        
        .results-stats {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
          margin-top: 15px;
        }
        
        .stat-item {
          text-align: center;
        }
        
        .stat-number {
          font-size: 24px;
          font-weight: bold;
          color: #28a745;
        }
        
        .stat-label {
          font-size: 14px;
          color: #666;
        }
        
        .warning-section {
          background: #fff3cd;
          border: 1px solid #ffeaa7;
          color: #856404;
          padding: 15px;
          border-radius: 4px;
          margin-bottom: 20px;
        }
        
        .warning-title {
          font-weight: bold;
          margin-bottom: 8px;
        }
      `}</style>

      <div className="page-header">
        <h2 className="page-title">Generate Sample Daily Allowance Data</h2>
        <p className="page-description">
          This tool will populate your pricing calendar with realistic daily allowance data for August and September 2025.
          Daily allowances represent the USD amount BackPass will cover for room bookings each day before users pay surcharges.
        </p>
      </div>

      <div className="info-section">
        <h3 className="info-title">What This Will Create:</h3>
        <ul className="info-list">
          <li>Daily allowances (USD) for August 1 - September 30, 2025</li>
          <li>Weekend allowance variations (higher allowances on Fridays and Saturdays)</li>
          <li>Midweek discounts (lower allowances on Tuesdays and Wednesdays)</li>
          <li>Peak season allowances (mid-August to mid-September with higher coverage amounts)</li>
          <li>Random variations to make data realistic (±15% allowance amounts)</li>
          <li>Albanian rates: Midweek: $10-$14, Base: $12-$18, Weekend: $15-$21, Peak: $18-$25</li>
          <li>10% of dates will be "locked" to demonstrate the lock feature</li>
          <li>Pricing history entries for all created daily allowances</li>
        </ul>
      </div>

      <div className="routes-section">
        <h3 className="routes-title">Routes Found ({routes.length}):</h3>
        {routes.length > 0 ? (
          <div className="routes-list">
            {routes.map(route => (
              <div key={route.id} className="route-item">
                {route.name}
              </div>
            ))}
          </div>
        ) : (
          <div className="no-routes">
            No routes found. Please create some routes in the "Manage Routes" section first.
          </div>
        )}
      </div>

      <div className="action-section">
        <div className="warning-section">
          <div className="warning-title">⚠️ Important:</div>
          This will create approximately {routes.length * 62} allotment records. 
          Make sure this is what you want before proceeding.
        </div>

        <button
          className="generate-button"
          onClick={populateSampleData}
          disabled={loading || routes.length === 0}
        >
          {loading ? 'Generating Sample Data...' : 'Generate Sample Data'}
        </button>

        {loading && (
          <div className="progress-section">
            <div className="progress-text">{progress}</div>
          </div>
        )}

        {results && (
          <div className={`results-section ${results.success ? 'results-success' : 'results-error'}`}>
            <div className="results-title">
              {results.success ? '✅ Sample Data Generated Successfully!' : '❌ Error Generating Sample Data'}
            </div>
            
            {results.success ? (
              <div>
                <div className="results-stats">
                  <div className="stat-item">
                    <div className="stat-number">{results.totalRecords}</div>
                    <div className="stat-label">Total Records</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">{results.routeCount}</div>
                    <div className="stat-label">Routes Processed</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">{results.dateCount}</div>
                    <div className="stat-label">Days Covered</div>
                  </div>
                </div>
                <p style={{marginTop: '20px'}}>
                  <strong>Next steps:</strong><br/>
                  • Visit <strong>/admin/pricing-calendar</strong> to see your data in the interactive calendar<br/>
                  • Make test bookings to see transaction tracking in <strong>/admin/transactions</strong><br/>
                  • Try bulk editing dates and individual date modifications
                </p>
              </div>
            ) : (
              <div>
                <strong>Error:</strong> {results.error}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminSampleData;